/**
 * Agent Gallery — Card grid view for visual browsing
 *
 * Same data as Hub but displayed as cards with icons, descriptions,
 * trigger badges, and usage stats. Good for discovering agents.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Search, Bot, Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AgentStatusBadge, OwnershipBadge, TriggerBadges } from '@/components/agents';
import { AgentIcon } from '@/components/shared/icon-picker';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  AGENT_CATEGORY_LABELS,
  getAgentCategoryStyle,
  type AgentStatus,
  type AgentCategory,
} from '@/lib/mock-agents-data';
import { useAgents } from '@/lib/use-agents';

export const Route = createFileRoute('/agents/gallery')({
  component: AgentGalleryPage,
});

function AgentGalleryPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  // Real-data: custom_agents
  const { data: realAgents = [] } = useAgents('all');
  const agents = realAgents;
  const [categoryFilter, setCategoryFilter] = useState<'all' | AgentCategory>('all');

  const filtered = agents.filter(a => {
    if (categoryFilter !== 'all' && a.category !== categoryFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q);
  });

  return (
    <div className="p-6 space-y-6">
      {/* Top bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search agents..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={categoryFilter} onValueChange={v => setCategoryFilter(v as 'all' | AgentCategory)}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(AGENT_CATEGORY_LABELS).map(([key, val]) => (
              <SelectItem key={key} value={key}>{val.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Card grid */}
      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(agent => (
            <Card
              key={agent.id}
              className="hover:border-primary/30 transition-colors cursor-pointer group"
              onClick={() => navigate({ to: '/agents/$agentId', params: { agentId: agent.id } })}
            >
              <CardContent className="pt-4 pb-3 space-y-3">
                {/* Header: icon + name + status */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <AgentIcon iconName={agent.icon} colorName={agent.iconColor} size="lg" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium truncate">{agent.name}</p>
                        {agent.isBuiltin && <Lock className="h-2.5 w-2.5 text-muted-foreground shrink-0" />}
                      </div>
                      <OwnershipBadge ownership={agent.ownership} />
                    </div>
                  </div>
                  <AgentStatusBadge status={agent.status} />
                </div>

                {/* Description */}
                <p className="text-[11px] text-muted-foreground line-clamp-2" title={agent.description}>{agent.description}</p>

                {/* Badges */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  {/* #1107 — defensive lookup; see comment in agents/index.tsx. */}
                  <Badge variant="outline" className={cn('text-[9px]', getAgentCategoryStyle(agent.category).color)}>
                    {getAgentCategoryStyle(agent.category).label}
                  </Badge>
                  <TriggerBadges triggers={agent.triggerModes} />
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-2 text-center pt-1">
                  <div>
                    <p className="text-xs font-semibold">{agent.tools.length}</p>
                    <p className="text-[10px] text-muted-foreground">Tools</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold">{agent.skills.length}</p>
                    <p className="text-[10px] text-muted-foreground">Skills</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold">{agent.metrics.totalQueries.toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">Queries</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold">{Math.round(agent.metrics.successRate * 100)}%</p>
                    <p className="text-[10px] text-muted-foreground">Success</p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-0.5">
                  <span>By {agent.createdBy}</span>
                  <Badge variant="secondary" className="text-[9px]">v{agent.version}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="border border-dashed rounded-lg p-8 flex flex-col items-center justify-center text-center">
          <Bot className="h-8 w-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm font-medium text-muted-foreground">No agents matching your search</p>
          <p className="text-xs text-muted-foreground/70 mt-1 mb-3">Try adjusting your filters or create a new agent</p>
          <Button variant="outline" size="sm" onClick={() => navigate({ to: '/agents/new' })}>
            New Agent
          </Button>
        </div>
      )}
    </div>
  );
}
